<template lang="pug">
v-dialog(v-model="dialog" width="1200" persistent scrollable)
    template(v-slot:activator="{ on, attrs }")
        v-btn.ml-2(small color="info" dark v-bind="attrs" v-on="on")
            v-icon(small left) mdi-chart-bar
            | Analytics
    v-card(v-if="dialog")
        v-card-title
            span Promo Analytics: {{ item.name || item.code }}
            v-spacer
            v-btn(icon @click="dialog = false")
                v-icon mdi-close
        v-card-text(v-if="loading")
            v-progress-linear(indeterminate color="primary")
            center.pa-5
                span Loading analytics...
        v-card-text(v-else-if="analytics")
            v-row
                // Overview Stats
                v-col(cols="12" md="3")
                    v-card(outlined)
                        v-card-text
                            div.text-center
                                div.text-h4.primary--text {{ analytics.overview.total_redemptions }}
                                div.text-caption Total Redemptions
                v-col(cols="12" md="3")
                    v-card(outlined)
                        v-card-text
                            div.text-center
                                div.text-h4.info--text {{ analytics.overview.total_clicks }}
                                div.text-caption Total Clicks
                v-col(cols="12" md="3")
                    v-card(outlined)
                        v-card-text
                            div.text-center
                                div.text-h4.success--text {{ analytics.overview.conversion_rate }}%
                                div.text-caption Conversion Rate
                v-col(cols="12" md="3")
                    v-card(outlined)
                        v-card-text
                            div.text-center
                                div.text-h4.warning--text {{ analytics.overview.total_points_distributed }}
                                div.text-caption Points Distributed

            v-row
                // Redemption Sources
                v-col(cols="12" md="6")
                    v-card(outlined)
                        v-card-title.subtitle-1 Redemption Sources
                        v-card-text
                            v-simple-table(dense)
                                template(v-slot:default)
                                    thead
                                        tr
                                            th Source
                                            th Count
                                            th Percentage
                                    tbody
                                        tr
                                            td
                                                v-icon(small color="primary") mdi-qrcode
                                                |  QR Scans
                                            td {{ analytics.sources.qr_scans }}
                                            td {{ calculatePercentage(analytics.sources.qr_scans, analytics.overview.total_redemptions) }}%
                                        tr
                                            td
                                                v-icon(small color="info") mdi-link-variant
                                                |  Direct Links
                                            td {{ analytics.sources.link_clicks }}
                                            td {{ calculatePercentage(analytics.sources.link_clicks, analytics.overview.total_redemptions) }}%
                                        tr
                                            td
                                                v-icon(small color="success") mdi-cellphone-link
                                                |  Deep Links
                                            td {{ analytics.sources.deeplinks }}
                                            td {{ calculatePercentage(analytics.sources.deeplinks, analytics.overview.total_redemptions) }}%

                // Device Breakdown
                v-col(cols="12" md="6")
                    v-card(outlined)
                        v-card-title.subtitle-1 Device Breakdown
                        v-card-text
                            v-simple-table(dense)
                                template(v-slot:default)
                                    thead
                                        tr
                                            th Device
                                            th Count
                                            th Percentage
                                    tbody
                                        tr
                                            td
                                                v-icon(small) mdi-apple
                                                |  iOS
                                            td {{ analytics.devices.ios }}
                                            td {{ calculatePercentage(analytics.devices.ios, analytics.overview.total_redemptions) }}%
                                        tr
                                            td
                                                v-icon(small) mdi-android
                                                |  Android
                                            td {{ analytics.devices.android }}
                                            td {{ calculatePercentage(analytics.devices.android, analytics.overview.total_redemptions) }}%
                                        tr
                                            td
                                                v-icon(small) mdi-web
                                                |  Web
                                            td {{ analytics.devices.web }}
                                            td {{ calculatePercentage(analytics.devices.web, analytics.overview.total_redemptions) }}%

            v-row
                // Timeline Chart
                v-col(cols="12")
                    v-card(outlined)
                        v-card-title.subtitle-1 
                            | Redemption Timeline
                            v-spacer
                            v-btn-toggle(v-model="timelineRange" dense)
                                v-btn(small value="7") 7 Days
                                v-btn(small value="30") 30 Days
                                v-btn(small value="90") 90 Days
                        v-card-text
                            div(v-if="timelineData.length > 0" style="height: 300px;")
                                canvas(ref="timelineChart")
                            div(v-else)
                                v-alert(type="info" text) No timeline data available

            v-row
                // Geography
                v-col(cols="12" md="6")
                    v-card(outlined)
                        v-card-title.subtitle-1 Top Countries
                        v-card-text
                            v-simple-table(dense)
                                template(v-slot:default)
                                    thead
                                        tr
                                            th Country
                                            th Redemptions
                                    tbody
                                        tr(v-for="country in analytics.geography.top_countries" :key="country.country")
                                            td {{ country.country }}
                                            td {{ country.redemption_count }}
                            div(v-if="!analytics.geography.top_countries || analytics.geography.top_countries.length === 0")
                                v-alert(type="info" text dense) No geographic data available

                v-col(cols="12" md="6")
                    v-card(outlined)
                        v-card-title.subtitle-1 Top Cities
                        v-card-text
                            v-simple-table(dense)
                                template(v-slot:default)
                                    thead
                                        tr
                                            th City
                                            th Redemptions
                                    tbody
                                        tr(v-for="city in analytics.geography.top_cities" :key="city.city")
                                            td {{ city.city }}, {{ city.country }}
                                            td {{ city.redemption_count }}
                            div(v-if="!analytics.geography.top_cities || analytics.geography.top_cities.length === 0")
                                v-alert(type="info" text dense) No city data available

            v-row(v-if="analytics.utm_tracking.campaigns && analytics.utm_tracking.campaigns.length > 0")
                v-col(cols="12")
                    v-card(outlined)
                        v-card-title.subtitle-1 UTM Campaign Tracking
                        v-card-text
                            v-simple-table(dense)
                                template(v-slot:default)
                                    thead
                                        tr
                                            th Campaign
                                            th Source
                                            th Medium
                                            th Redemptions
                                            th Unique Users
                                    tbody
                                        tr(v-for="utm in analytics.utm_tracking.campaigns" :key="utm.utm_campaign")
                                            td {{ utm.utm_campaign }}
                                            td {{ utm.utm_source }}
                                            td {{ utm.utm_medium }}
                                            td {{ utm.redemption_count }}
                                            td {{ utm.unique_users }}

            v-row
                v-col(cols="12")
                    v-card(outlined)
                        v-card-title.subtitle-1 Recent Redemptions
                        v-card-text
                            v-simple-table(dense)
                                template(v-slot:default)
                                    thead
                                        tr
                                            th User
                                            th Location
                                            th Source
                                            th Device
                                            th Date
                                    tbody
                                        tr(v-for="redemption in analytics.recent_redemptions.slice(0, 10)" :key="redemption.id")
                                            td
                                                v-avatar(size="24" v-if="redemption.user")
                                                    img(:src="redemption.user.profile_picture" v-if="redemption.user.profile_picture")
                                                    v-icon(v-else small) mdi-account
                                                span.ml-2 {{ redemption.user ? redemption.user.name : 'Unknown' }}
                                            td {{ redemption.user ? redemption.user.location : 'Unknown' }}
                                            td
                                                v-chip(x-small :color="getSourceColor(redemption.source)") {{ redemption.source }}
                                            td {{ redemption.device_type || 'Unknown' }}
                                            td {{ formatDate(redemption.created_at) }}

        v-card-text(v-else-if="error")
            v-alert(type="error") {{ error }}
</template>

<script>
import Chart from 'chart.js';
import feathersClient from '@/plugins/feathers-client';

export default {
    props: {
        item: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            error: null,
            analytics: null,
            timelineRange: '30',
            chart: null
        };
    },
    computed: {
        timelineData() {
            if (!this.analytics || !this.analytics.timeline) return [];
            return this.analytics.timeline;
        }
    },
    watch: {
        dialog(val) {
            if (val) {
                this.loadAnalytics();
            } else {
                // Destroy chart when dialog closes
                if (this.chart) {
                    this.chart.destroy();
                    this.chart = null;
                }
            }
        },
        timelineRange() {
            if (this.dialog) {
                this.loadAnalytics();
            }
        },
        timelineData() {
            this.$nextTick(() => {
                this.renderChart();
            });
        }
    },
    methods: {
        async loadAnalytics() {
            this.loading = true;
            this.error = null;
            try {
                const response = await feathersClient.service('api/promo-analytics').get(this.item.id, {
                    query: {
                        days: parseInt(this.timelineRange)
                    }
                });
                this.analytics = response;
            } catch (err) {
                console.error('Failed to load analytics:', err);
                this.error = err.message || 'Failed to load analytics data';
            } finally {
                this.loading = false;
            }
        },
        calculatePercentage(value, total) {
            if (!total || total === 0) return 0;
            return Math.round((value / total) * 100);
        },
        getSourceColor(source) {
            const colors = {
                qr: 'primary',
                link: 'info',
                deeplink: 'success'
            };
            return colors[source] || 'grey';
        },
        formatDate(date) {
            if (!date) return 'N/A';
            return new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        renderChart() {
            if (this.chart) {
                this.chart.destroy();
            }

            if (!this.$refs.timelineChart || !this.timelineData || this.timelineData.length === 0) {
                return;
            }

            const ctx = this.$refs.timelineChart.getContext('2d');
            
            const labels = this.timelineData.map(d => d.date);
            const qrData = this.timelineData.map(d => d.qr_count || 0);
            const linkData = this.timelineData.map(d => d.link_count || 0);
            const deeplinkData = this.timelineData.map(d => d.deeplink_count || 0);

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'QR Scans',
                            data: qrData,
                            borderColor: '#1976D2',
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            fill: true
                        },
                        {
                            label: 'Direct Links',
                            data: linkData,
                            borderColor: '#00ACC1',
                            backgroundColor: 'rgba(0, 172, 193, 0.1)',
                            fill: true
                        },
                        {
                            label: 'Deep Links',
                            data: deeplinkData,
                            borderColor: '#388E3C',
                            backgroundColor: 'rgba(56, 142, 60, 0.1)',
                            fill: true
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    tooltips: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            footer: function(tooltipItems) {
                                const total = tooltipItems.reduce((sum, item) => sum + item.yLabel, 0);
                                return 'Total: ' + total;
                            }
                        }
                    },
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                precision: 0
                            }
                        }]
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            });
        }
    }
}
</script>
